"use client";

import  {CardProps, Divider, Select, Snippet} from "@heroui/react";

import React from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Tabs,
  Tab,
  ScrollShadow,
  CardFooter,
  SelectItem,
  Pagination,
} from "@heroui/react";
import {Icon} from "@iconify/react";
import CopyIc from './Copy.svg'
import NotificationItem from "./notification-item";
import Typography from "../typography/Typography";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslations } from "../../contexts/translation.context";

type Selection = 'all' | Set<React.Key>;

interface AppNotification {
  id: string;
  created_at: string;
  message: string;
  alert_message: string | null;
  was_read: boolean;
  owner: string;
}

enum NotificationTabs {
  All = "all",
  Unread = "unread",
  Read = "read",
}

export default function Component(props: CardProps) {
  const { notifications, getNotifications, markNotificationAsRead, markAllNotificationsAsRead } = useAuth();
  const { t } = useTranslations();
  const [activeTab, setActiveTab] = React.useState<NotificationTabs>(NotificationTabs.All);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] = React.useState(10);
  
  const isPartner = window.location.pathname.startsWith('/partner');
  const botLink = t('notificationsCard.telegramBotLink');
  const telegramDescription = t('notificationsCard.telegramDescription').replace('{botLink}', botLink);
  const snippetTooltip = t('notificationsCard.snippetTooltip');
  const notificationsTitle = t('notificationsCard.notificationsTitle');
  const markAll = t('notificationsCard.markAll');
  const tabsAriaLabel = t('notificationsCard.tabsAriaLabel');
  const tabUnread = t('notificationsCard.tabUnread');
  const tabRead = t('notificationsCard.tabRead');
  const tabAll = t('notificationsCard.tabAll');
  const listAriaLabel = t('notificationsCard.listAria');
  const noNotificationsTitle = t('notificationsCard.noNotificationsTitle');
  const noNotificationsDescription = t('notificationsCard.noNotificationsDescription');
  const perPage = t('notificationsCard.perPage');
  const pageSizeAria = t('notificationsCard.pageSizeAria');
  const systemName = t('notificationsCard.systemName');
  const telegramTitle = t('notificationsCard.telegramTitle');
  const telegramKey = t('notificationsCard.telegramKey');
  const formatCountLabel = React.useCallback(
    (key: string, count: number) => t(key).replace('{count}', count.toString()),
    [t],
  );

  // Мемоизируем функции для предотвращения лишних ререндеров
  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = React.useCallback((keys: Selection) => {
    if (keys === 'all') {
      return;
    }
    const newPageSize = parseInt(Array.from(keys)[0] as string, 10);
    setCurrentPageSize(newPageSize);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении размера
  }, []);

  const handleMarkAllAsRead = React.useCallback(async () => {
    await markAllNotificationsAsRead();
  }, [markAllNotificationsAsRead]);

  const handleMarkAsRead = React.useCallback(async (id: string) => {
    await markNotificationAsRead(id);
  }, [markNotificationAsRead]);

  // Загружаем уведомления только при изменении страницы или размера страницы
  React.useEffect(() => {
    getNotifications(currentPage, currentPageSize);
  }, [currentPage, currentPageSize, getNotifications]);

  // Автоматическое обновление уведомлений каждые 10 секунд
  React.useEffect(() => {
    const interval = setInterval(() => {
      getNotifications(currentPage, currentPageSize);
    }, 10000); // 10 секунд

    // Очистка интервала при размонтировании компонента
    return () => clearInterval(interval);
  }, [currentPage, currentPageSize, getNotifications]); // Зависимости для интервала

  const notificationList = React.useMemo(() => (notifications?.objects ?? []) as AppNotification[], [notifications]);

  const filteredNotifications = React.useMemo(() => {
    switch (activeTab) {
      case NotificationTabs.Unread:
        return notificationList.filter((n) => !n.was_read);
      case NotificationTabs.Read:
        return notificationList.filter((n) => n.was_read);
      default:
        return notificationList;
    }
  }, [notificationList, activeTab]);
  const totalPages = notifications ? Math.ceil(notifications.count / currentPageSize) : 0;
  const unreadCount = notificationList.filter((n) => !n.was_read).length || 0;
  const readCount = notificationList.filter((n) => n.was_read).length || 0;
  const allCount = notifications?.count || 0;
  const unreadChipLabel = formatCountLabel('notificationsCard.tabUnreadAria', unreadCount);
  const readChipLabel = formatCountLabel('notificationsCard.tabReadAria', readCount);
  const allChipLabel = formatCountLabel('notificationsCard.tabAllAria', allCount);

  return (
    <Card className="w-full min-w-[439px] py-[8px]" {...props}>
      <CardHeader className="flex flex-col px-0 pb-0">
        <div className="flex w-full items-center justify-between px-[16px] py-[12px] pb-[20px]">
          <div className="flex flex-col gap-1">
            <Typography variant="label1">{telegramTitle}</Typography>
            <Typography variant="body3" className="text-[#A1A1AA]">{telegramDescription}
              <span>
                <Snippet
                  tooltipProps={{
                    color: "foreground",
                    content: snippetTooltip,
                    disableAnimation: true,
                    placement: "right",
                    closeDelay: 0,
                  }}
                  symbol=''
                  copyIcon={<img src={CopyIc} />}
                  classNames={{
                    base: "text-[#A1A1AA] bg-[none] p-[0px] h-[16px] gap-[0px]",
                  }}
                >
                {telegramKey}
                </Snippet>
              </span>
            </Typography>
          </div>

        </div>
        <Divider/>
        <div className="p-[16px] w-full flex justify-between gap-[10px]">
          <Typography variant="label1" className="">{notificationsTitle}</Typography>
          <Typography 
            variant="body2" 
            className="!text-[#006FEE] cursor-pointer hover:opacity-80"
            onClick={handleMarkAllAsRead}
          >
            {markAll}
          </Typography>
        </div>
        <div className={`w-full justify-center items-center ${isPartner ? "partner-tabs" : ""}`}>
          {/* @ts-ignore */}
          {isPartner && <style jsx global>{`
            .partner-tabs [data-selected="true"] span,
            .partner-tabs [data-selected="true"] div,
            .partner-tabs [data-selected="true"] * {
              color: #006FEE !important;
            }
            .partner-tabs [data-selected=true] span,
            .partner-tabs [data-selected=true] div,
            .partner-tabs [data-selected=true] * {
              color: #006FEE !important;
            }
            .partner-tabs .data-\\[selected\\=true\\] span,
            .partner-tabs .data-\\[selected\\=true\\] div,
            .partner-tabs .data-\\[selected\\=true\\] * {
              color: #006FEE !important;
            }
          `}</style>}
          <Tabs
            aria-label={tabsAriaLabel}
            classNames={{
              base: "w-full",
              tabList: "gap-6 px-6 py-0 w-full justify-center flex relative rounded-none border-b border-divider",
              cursor: isPartner ? "w-full !bg-[#006FEE]" : "w-full",
              tab: "max-w-fit px-2 h-12",
            }}
            color="primary"
            selectedKey={activeTab}
            variant="underlined"
            onSelectionChange={(selected: React.Key) => setActiveTab(selected as NotificationTabs)}
            style={isPartner ? {
              '--nextui-primary': '#006FEE'
            } as React.CSSProperties : undefined}
          >
          <Tab
            key="unread"
            title={
              <div className="flex items-center space-x-2">
                <span>{tabUnread}</span>
                <span
                  className="inline-flex min-w-[24px] items-center justify-center rounded-full bg-[#f4f4f5] px-[6px] py-[2px] text-[12px]"
                  aria-label={unreadChipLabel}
                >
                  {unreadCount}
                </span>
              </div>
            }
          />
          <Tab
            key="read"
            title={
              <div className="flex items-center space-x-2">
                <span>{tabRead}</span>
                <span
                  className="inline-flex min-w-[24px] items-center justify-center rounded-full bg-[#f4f4f5] px-[6px] py-[2px] text-[12px]"
                  aria-label={readChipLabel}
                >
                  {readCount}
                </span>
              </div>
            }
          />
          <Tab key="all" title={
              <div className="flex items-center space-x-2">
                <span>{tabAll}</span>
                <span
                  className="inline-flex min-w-[24px] items-center justify-center rounded-full bg-[#f4f4f5] px-[6px] py-[2px] text-[12px]"
                  aria-label={allChipLabel}
                >
                  {allCount}
                </span>
              </div>
            } />
          </Tabs>
        </div>
      </CardHeader>
      <CardBody className="w-full gap-0 p-0">
        <ScrollShadow className="h-[500px] w-full" aria-label={listAriaLabel}>
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification: AppNotification) => (
              <NotificationItem 
                key={notification.id} 
                id={notification.id}
                isRead={notification.was_read}
                avatar="https://i.pravatar.cc/150?u=a04258114e29026708c"
                description={notification.message}
                name={systemName}
                time={new Date(notification.created_at).toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                type="default"
                onMarkAsRead={() => handleMarkAsRead(notification.id)}
              />
            ))
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2">
              <Icon className="text-default-400" icon="solar:bell-off-linear" width={40} aria-label={noNotificationsTitle} />
              <p className="text-small text-default-400">{noNotificationsDescription}</p>
            </div>
          )}
        </ScrollShadow>
      </CardBody>
      <CardFooter className="p-[16px]">
        {notifications && notifications.count > 0 && (
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center gap-[10px]">
              <Typography variant="body4" className="text-[#71717A] text-sm">
                {perPage}
              </Typography>
              <Select
                size="sm"
                selectedKeys={new Set<React.Key>([currentPageSize.toString()])}
                onSelectionChange={handlePageSizeChange}
                className="w-20"
                aria-label={pageSizeAria}
              >
                <SelectItem key="10" textValue="10">10</SelectItem>
                <SelectItem key="20" textValue="20">20</SelectItem>
                <SelectItem key="50" textValue="50">50</SelectItem>
              </Select>
            </div>
            
            <Pagination
              total={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              boundaries={-1}
              isCompact 
              showControls
              color="primary"
              size="sm"
              classNames={isPartner ? {
                cursor: "font-medium !bg-[#006FEE] !text-white",
                item: "hover:!bg-gray-100"
              } : {}}
            />
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
